import { useState, useRef, useEffect } from "react";
import { ICalendarEntry } from "../../customTypings/calendar/CalendarEntry";
import { calendarEventService } from "../../services";
import { showToastSuccess, showToastError } from "../../utils";
import { IEvent } from "../../components/features/calendar/EventCalendar";
import { transformCalendarEntriesToEvents, createEventFromClickInfo, extractIdentityIdsForUpdate, getEventEndDate } from "../../utils/event/eventUtils";
import dayjs from "dayjs";

export const useEventCalendar = (
  identityIds: string[],
  updateEventAllowed: boolean,
  setColors?: any
) => {
  const calendarRef = useRef<any>();
  const [events, setEvents] = useState<IEvent[]>([]);
  const [currentIdentityIds, setCurrentIdentityIds] = useState(identityIds);

  function getAllEntries(object: Record<string, any>) {
    calendarEventService
      .getAllEntries(object)
      .then((events: ICalendarEntry[]) => {
        const allEvents = transformCalendarEntriesToEvents(events, updateEventAllowed, setColors);
        setEvents(allEvents);
      });
  }

  function updateEvents(start?: Date, end?: Date) {
    var startToUse = start;
    var endToUse = end;

    if (calendarRef.current) {
      startToUse = calendarRef.current.getApi().view.currentStart;
      endToUse = calendarRef.current.getApi().view.currentEnd;
    }
    const filterData: Record<string, any> = {
      fromDate: startToUse,
      toDate: endToUse,
    };
    filterData["identityIds"] = identityIds;
    getAllEntries(filterData);
  }

  const handleEventChange = async (info: any) => {
    const eventIdentityIds = extractIdentityIdsForUpdate(info);
    const end = getEventEndDate(info.event.start, info.event.end, info.event.allDay);

    const updatedCalendarEntry = {
      id: info.event.groupId,
      taskId: info.event.extendedProps.taskId,
      title: info.event.title,
      description: info.event.extendedProps.description,
      appointmentTypeId: info.event.extendedProps.appointmentTypeId,
      startDate: dayjs.utc(dayjs(info.event.start).format("YYYY-MM-DD HH:mm")).toDate(),
      endDate: dayjs.utc(end.format("YYYY-MM-DD HH:mm")).toDate(),
      isAllDay: info.event.allDay,
      identityIds: eventIdentityIds,
      color: undefined,
    };

    try {
      const response = await calendarEventService.update(updatedCalendarEntry);
      if (response.status !== "Failure") {
        updateEvents();
        showToastSuccess("Event Updated Successfully");
      } else {
        showToastError(response.message);
      }
    } catch (error: any) {
      if (error.status === 400 && error.errors) {
        Object.keys(error.errors).forEach((key) => {
          const messages = error.errors[key];
          if (Array.isArray(messages)) {
            messages.forEach((message) => showToastError(message));
          }
        });
      } else {
        showToastError("Something went wrong. Please try again.");
      }
      info.revert();
    }
  };

  useEffect(() => {
    if (
      calendarRef.current &&
      !(
        currentIdentityIds.length === identityIds.length &&
        currentIdentityIds.every((value, index) => value === identityIds[index])
      )
    ) {
      setCurrentIdentityIds(identityIds);
      updateEvents();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identityIds]);

  return {
    calendarRef,
    events,
    updateEvents,
    handleEventChange,
  };
};
